import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NotesCard from "./NotesCard";
import type { OrganizerCardProps } from "../../../types/types";
import { LocationIcon } from "../../../../../../components/icons";
import { tokens } from "../../../../../../theme";
import { WhatsApp, Phone } from "@mui/icons-material";
import ContactDetailsDialog from "./ContactDetailsDialog";
import WhatsAppDialog from "./WhatsAppDialog";
import PaymentReminderDialog from "./PaymentReminderDialog";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import type { BookingData } from "../../../../types/view-booking";

interface ClientInfoCardProps {
  client?: OrganizerCardProps;
  currentUserName?: string;
  onViewAllBookings?: () => void;
  loading?: boolean;
  bookingType?: string;
  funeralHome?: {
    name: string;
    companyBranch: string | null;
  } | null;
  bookingData?: BookingData | null;
}

function ClientInfoSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circular" width={56} height={56} />
        <Stack spacing={1} flex={1}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={18} />
        </Stack>
      </Stack>
      <Skeleton variant="rounded" width="100%" height={40} />
      <Skeleton variant="rounded" width="100%" height={60} />
      <Skeleton variant="rounded" width="100%" height={60} />
    </Stack>
  );
}

export default function ClientInfoCard({
  client,
  currentUserName = "Muzukuru Staff",
  onViewAllBookings,
  loading,
  bookingType,
  funeralHome,
  bookingData,
}: ClientInfoCardProps) {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const navigate = useNavigate();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isPaymentReminderOpen = searchParams.get("paymentReminder") === "1";

  const isFuneralHomeBooking = bookingType?.toUpperCase() === "FUNERAL_HOME";
  const hasContact = Boolean(client && client.id !== 0 && client.name !== "N/A");

  const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  };

  const handleCall = () => {
    if (client?.phone) window.location.href = `tel:${client.phone}`;
  };

  const handleWhatsApp = () => {
    setIsWhatsAppDialogOpen(true);
  };

  return (
    <>
      <Paper
        sx={{
          flex: 1,
          borderRadius: "12px",
          p: 2,
          boxShadow: 0,
          height: "100%",
          bgcolor: colors.primary[900],
        }}
      >
        {loading || !client ? (
          <ClientInfoSkeleton />
        ) : isFuneralHomeBooking && !hasContact && funeralHome ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: colors.primary[400],
                  fontWeight: 700,
                  fontSize: "14px",
                }}
              >
                {getInitials(funeralHome.name)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px",
                    color: colors.primary[700],
                  }}
                >
                  {funeralHome.name}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: colors.primary[300],
                  }}
                >
                  Funeral Home
                </Typography>
              </Box>
            </Stack>

            {funeralHome.companyBranch && (
              <Box
                sx={{
                  bgcolor: colors.primary[800],
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", color: colors.primary[300] }}
                >
                  Company / Branch
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: colors.primary[700],
                  }}
                >
                  {funeralHome.companyBranch}
                </Typography>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              onClick={() => setIsContactDialogOpen(true)}
              sx={{ cursor: "pointer", "&:hover": { opacity: 0.85 } }}
            >
              <Avatar
                src={client?.contact_image || undefined}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: colors.primary[400],
                  fontWeight: 700,
                  fontSize: "14px",
                }}
              >
                {!client?.contact_image && getInitials(client?.name)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px",
                    color: colors.primary[700],
                  }}
                >
                  {client?.name}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: colors.primary[300],
                  }}
                >
                  {client?.relation}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  onClick={handleCall}
                  disabled={!client?.phone}
                  sx={{
                    bgcolor: colors.secondary[500] + "50",
                    width: 30,
                    height: 30,
                    "&:hover": { bgcolor: colors.secondary[500] },
                  }}
                >
                  <Phone sx={{ fontSize: 16, color: colors.primary[400] }} />
                </IconButton>
                <IconButton
                  onClick={handleWhatsApp}
                  disabled={!client?.phone}
                  sx={{
                    bgcolor: colors.secondary[500] + "50",
                    width: 30,
                    height: 30,
                    "&:hover": { bgcolor: colors.secondary[500] },
                  }}
                >
                  <WhatsApp sx={{ fontSize: 16, color: colors.primary[400] }} />
                </IconButton>
              </Stack>
            </Stack>

            {client?.address?.trim() && client.address !== "N/A" && (
              <Box
                sx={{
                  bgcolor: colors.primary[800],
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  gap: 1,
                }}
              >
                <LocationIcon
                  sx={{ fontSize: 16, color: colors.primary[300] }}
                />
                <Typography
                  sx={{ fontSize: "13px", color: colors.primary[700] }}
                >
                  {client.address}
                </Typography>
              </Box>
            )}

            {client?.source_of_booking === "referral" &&
              client.referral_partner && (
                <Box
                  sx={{
                    bgcolor: colors.primary[800],
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", color: colors.primary[300] }}
                  >
                    Referred by
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: colors.primary[700],
                    }}
                  >
                    {client.referral_partner.display_name || "—"}
                  </Typography>
                </Box>
              )}

            {isFuneralHomeBooking && funeralHome && (
              <Box
                sx={{
                  bgcolor: colors.primary[800],
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{ fontSize: "13px", color: colors.primary[300] }}
                  >
                    Funeral Home
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: colors.primary[700],
                    }}
                  >
                    {funeralHome.name}
                  </Typography>
                </Box>
                {funeralHome.companyBranch && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      sx={{ fontSize: "13px", color: colors.primary[300] }}
                    >
                      Company / Branch
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: colors.primary[700],
                      }}
                    >
                      {funeralHome.companyBranch}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <NotesCard
              title="Special Notes"
              content={client?.specialNotes || ""}
            />
            <NotesCard title="Notes" content={client?.notes || ""} />
          </Stack>
        )}
      </Paper>

      <ContactDetailsDialog
        open={isContactDialogOpen}
        client={client}
        currentUserName={currentUserName}
        onClose={() => setIsContactDialogOpen(false)}
        onWhatsAppClick={() => {
          setIsContactDialogOpen(false);
          setIsWhatsAppDialogOpen(true);
        }}
        onBookingClick={(bookingId) => {
          setIsContactDialogOpen(false);
          navigate(`/bookings/${bookingId}`);
        }}
        onViewAllBookings={() => {
          setIsContactDialogOpen(false);
          onViewAllBookings?.();
        }}
      />

      <WhatsAppDialog
        open={isWhatsAppDialogOpen}
        onClose={() => setIsWhatsAppDialogOpen(false)}
        client={client}
        currentUserName={currentUserName}
        bookingData={bookingData}
      />
      <PaymentReminderDialog
        open={isPaymentReminderOpen}
        bookingId={bookingData?.id}
        clientName={client?.name}
        clientEmail={client?.email}
        clientPhone={client?.phone}
        onClose={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("paymentReminder");
          setSearchParams(next, { replace: true });
        }}
      />
    </>
  );
}
